import type { Claim, CoveragePolicy, CoverageState, Endorsement, EndorsementType, Scenario } from "@ti/core";
import {
  Ban,
  Bot,
  Building2,
  CalendarX,
  CircleArrowDown,
  CirclePlus,
  Clock,
  FilePen,
  FileText,
  Hourglass,
  RotateCcw,
  Scale,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  TrendingUp,
  User,
} from "lucide-react";
import type { ReactNode } from "react";
import type { BadgeTone } from "@/components/ui/badge";

const DAY = 86_400_000;

// ─── Policy ──────────────────────────────────────────────────────────────────

export const POLICY_STATUS: Record<CoveragePolicy["status"], { label: string; tone: BadgeTone; icon: ReactNode; help: string }> = {
  quote: { label: "Quote", tone: "neutral", icon: <FileText />, help: "Priced but not submitted. Nothing is covered until the policy is bound." },
  pending_bind: { label: "Pending bind", tone: "warning", icon: <Hourglass />, help: "Submitted to the carrier. Coverage starts when the carrier binds the policy." },
  active: { label: "Active", tone: "good", icon: <ShieldCheck />, help: "Bound by the carrier. You can file claims against this policy — and, for third-party scenarios, so can the people your agents affect." },
  expired: { label: "Expired", tone: "neutral", icon: <CalendarX />, help: "The policy period has ended. Claims for incidents inside the period can still be filed." },
  cancelled: { label: "Cancelled", tone: "critical", icon: <Ban />, help: "Cancelled before the end of the period. No new incidents are covered." },
};

/** Policies that currently provide cover (or are about to). */
export const isLivePolicy = (p: CoveragePolicy) => p.status === "active" || p.status === "pending_bind";

export const STAGES: Array<{ id: CoveragePolicy["stage"]; step: number; label: string; detail: string }> = [
  {
    id: "risk_platform",
    step: 1,
    label: "Risk platform",
    detail: "traces.insure provides scores, issues and evidence. Cover is arranged separately with your broker.",
  },
  {
    id: "telemetry_partner",
    step: 2,
    label: "Telemetry partner",
    detail: "The carrier underwrites and adjudicates. traces.insure supplies telemetry, the scenario registry and evidence packs.",
  },
  {
    id: "mga",
    step: 3,
    label: "MGA",
    detail: "traces.insure binds and administers coverage on the carrier's paper under delegated authority.",
  },
];

export const STAGE_LABEL: Record<CoveragePolicy["stage"], string> = {
  risk_platform: "Risk platform",
  telemetry_partner: "Telemetry partner",
  mga: "MGA",
};

export const MODE_LABEL: Record<CoveragePolicy["mode"], { label: string; help: string }> = {
  graduated: {
    label: "Graduated",
    help: "After a grace window expires, the scenario's sub-limit steps down with co-insurance before any exclusion.",
  },
  strict: {
    label: "Strict",
    help: "After a grace window expires, the scenario is excluded until the fix is verified.",
  },
};

export function daysBetween(fromIso: string, toIso: string | number) {
  const to = typeof toIso === "number" ? toIso : Date.parse(toIso);
  return Math.round((to - Date.parse(fromIso)) / DAY);
}

// ─── Scenario states ─────────────────────────────────────────────────────────

/** Calm, precise one-liners per coverage state (used in summaries and the scenario sheet). */
export const STATE_DESCRIPTION: Record<CoverageState, string> = {
  covered: "Monitored with a clean or verified track record. Full sub-limit applies.",
  grace: "An issue is open and your team is inside the grace window. Cover continues meanwhile.",
  mitigated: "A control the agent platform enforces neutralizes the issue. Covered while the control stays active.",
  verifying: "A fix has been declared. Replay, probes and a production soak are in progress.",
  graduated: "The grace window expired. A reduced sub-limit and co-insurance apply until remediated.",
  excluded: "Not remediated in time. Losses in this scenario are not covered until the fix is verified.",
  unobserved: "No telemetry or unconfirmed tool classification. Not covered until observed.",
};

/** Graduated schedule from docs/05 §3.3. `coverage` and `coinsurance` match `Scenario.graduatedStage`. */
export const GRADUATED_SCHEDULE: Array<{ id: string; window: string; coverage: number | null; coinsurance: number | null; note: string }> = [
  { id: "grace", window: "0 → grace deadline", coverage: 1, coinsurance: 0, note: "Critical issues: 50% of sub-limit during grace" },
  { id: "g1", window: "+0–7 days", coverage: 0.75, coinsurance: 0.1, note: "Graduated step 1" },
  { id: "g2", window: "+7–30 days", coverage: 0.5, coinsurance: 0.25, note: "Graduated step 2" },
  { id: "g3", window: "+30–90 days", coverage: 0.25, coinsurance: 0.5, note: "Graduated step 3" },
  { id: "excluded", window: "> 90 days", coverage: null, coinsurance: null, note: "Excluded — endorsement issued" },
];

export function graduatedStepLabel(stage: NonNullable<Scenario["graduatedStage"]>) {
  const idx = GRADUATED_SCHEDULE.findIndex((r) => r.coverage === stage.coverage && r.coinsurance === stage.coinsurance);
  return idx > 0 ? `Step ${idx}` : "Graduated";
}

export function graduatedText(stage: NonNullable<Scenario["graduatedStage"]>) {
  return `${Math.round(stage.coverage * 100)}% of sub-limit · ${Math.round(stage.coinsurance * 100)}% co-insurance`;
}

/**
 * Start of the grace window. Uses `Scenario.graceStartedAt`; for older records without it, falls back to
 * the matching `grace_started` endorsement, then to a 7-day window before the deadline.
 */
export function graceStartFor(s: Scenario, endorsements: Endorsement[] | undefined): string | null {
  if (!s.graceDeadline) return null;
  if (s.graceStartedAt) return s.graceStartedAt;
  const e = endorsements?.find((x) => x.type === "grace_started" && x.issueId && x.issueId === s.issueId);
  return e?.effectiveAt ?? new Date(Date.parse(s.graceDeadline) - 7 * DAY).toISOString();
}

// ─── Endorsements ────────────────────────────────────────────────────────────

export const ENDORSEMENT_META: Record<EndorsementType, { label: string; icon: ReactNode; tone: BadgeTone }> = {
  policy_bound: { label: "Policy bound", icon: <FilePen />, tone: "accent" },
  scenario_added: { label: "Scenario added", icon: <CirclePlus />, tone: "good" },
  scenario_excluded: { label: "Scenario excluded", icon: <ShieldOff />, tone: "critical" },
  scenario_reinstated: { label: "Scenario reinstated", icon: <RotateCcw />, tone: "good" },
  limit_increase: { label: "Limit increase", icon: <TrendingUp />, tone: "accent" },
  grace_started: { label: "Grace started", icon: <Clock />, tone: "warning" },
  mitigated: { label: "Mitigated", icon: <ShieldAlert />, tone: "info" },
  graduated_step: { label: "Graduated step", icon: <CircleArrowDown />, tone: "serious" },
  re_rating: { label: "Re-rating", icon: <Scale />, tone: "violet" },
};

export const ENDORSEMENT_TYPES = Object.keys(ENDORSEMENT_META) as EndorsementType[];

// ─── Claims ──────────────────────────────────────────────────────────────────

export const FILED_BY: Record<Claim["filedBy"], { label: string; icon: ReactNode; help: string }> = {
  end_user: { label: "End user", icon: <User />, help: "Filed by one of your end users on your public protection page." },
  customer: { label: "Customer", icon: <Building2 />, help: "Filed by your team as the policyholder." },
  auto_prompt: { label: "Auto-detected", icon: <Bot />, help: "A likely loss was detected from telemetry and a claim was opened automatically." },
};

/** Short name for the policyholder ("Acme Outfitters" → "Acme"), used for the "filed by" label. */
export function holderShortName(orgName: string | undefined) {
  return orgName?.trim().split(/\s+/)[0] || "Customer";
}

export const isOpenClaim = (c: Claim) => c.status !== "paid" && c.status !== "denied";

export function sumMoney<T>(items: T[] | undefined, pick: (t: T) => number) {
  return (items ?? []).reduce((acc, t) => acc + pick(t), 0);
}
